import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import Fuse from 'fuse.js';
import classNames from 'classnames';

import {
  useConfigStore, useAppStore, useWindowStore, useMapDataStore, useMapInteractionStore,
} from '../../stores';
import { useFavoriteButtons } from '../Personalization/Favorite/FavoriteButtonsContext';
import FormattedResidenceFilterData from './FormattedResidenceFilterData';
import formatAndMergeFilteredData from './FormatAndMergeFilteredData';
import { updatePendingSelections, getFilterResultsByType, createDetailBtnStyle } from './ResidenceFilterHelpers';
import { getContrastTextColorValue, decodeHtml } from '../../utils/common';

import SlidingPanesWidget from '../SlidingPanesWidget/SlidingPanesWidget';
import SlidingPane from '../SlidingPanesWidget/SlidingPane';
import ResidenceFilterOptionTags from './ResidenceFilterOptionTag';

const ResidenceFilterContent = ({
  showByFieldName,
}) => {
  const [activeSlidingPaneIndex, setActiveSlidingPaneIndex] = useState(0);
  const [fuseIndex, setFuseIndex] = useState(null);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [levelOfCareNames, setLevelOfCareNames] = useState({});
  const [originalFilterData, setOriginalFilterData] = useState({});
  const [pendingSelections, setPendingSelections] = useState({});
  const [paneWidths, setPaneWidths] = useState({});
  const [formattedMapFilterData, setFormattedMapFilterData] = useState({});

  // Zustand stores
  const config = useConfigStore((state) => state.config);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const windowWidth = useWindowStore((state) => state.windowWidth);
  const settings = useAppStore((state) => state.settings);
  const personalizationActive = useAppStore((state) => state.personalizationActive);
  const residenceStatusOptions = useMapDataStore((state) => state.residenceStatusOptions);
  const mapFilterData = useMapDataStore((state) => state.mapFilterData);

  const { platform } = settings || {};
  const { platformShowOccupancy } = featureFlags || {};
  const { defaultLevelOfCare } = config || {};

  // Map interaction store
  const activeCategoryFilter = useMapInteractionStore((state) => state.activeCategoryFilter);
  const activeFilterSelections = useMapInteractionStore((state) => state.activeFilterSelections);
  const changedUnits = useMapInteractionStore((state) => state.changedUnits);
  const setActiveCategoryFilter = useMapInteractionStore((state) => state.setActiveCategoryFilter);
  const setActiveFilterSelections = useMapInteractionStore((state) => state.setActiveFilterSelections);
  const activeLevelOfCareUuid = useMapInteractionStore((state) => state.activeLevelOfCareUuid);
  const setActiveLevelOfCareUuid = useMapInteractionStore((state) => state.setActiveLevelOfCareUuid);
  const setMapActiveUnitData = useMapInteractionStore((state) => state.setMapActiveUnitData);
  const setResultFloorPlans = useMapInteractionStore((state) => state.setResultFloorPlans);
  const validFloorPlanUuids = useMapInteractionStore((state) => state.validFloorPlanUuids);
  const preFormattedFilterData = useMapInteractionStore((state) => state.preFormattedFilterData);

  const { residenceFavorites } = useFavoriteButtons();

  // Sort locations alphabetically on SiteSeePage or PibPage
  const shouldSortLocationAlphabetically = platform === 'siteSee' || platform === 'page';

  // Track component mount status to prevent memory leaks
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // Check if there's only one level of care
  const levelOfCareEntries = Object.entries(levelOfCareNames);
  const hasSingleLevelOfCare = levelOfCareEntries.length === 1;
  const singleLevelOfCareUuid = hasSingleLevelOfCare ? levelOfCareEntries[0]?.[0] : null;

  const locData = formattedMapFilterData[activeLevelOfCareUuid]?.children;
  const detailsData = locData?.[activeCategoryFilter]?.children;

  // Remove the data we don't need for the category pane.
  const categoryData = locData;
  delete categoryData?.level_of_care;
  if (!platformShowOccupancy) {
    delete categoryData?.residence_status;
  }

  const shouldShowFavoriteResidences = ((personalizationActive && platform === 'yt') || platform === 'page')
    && showByFieldName !== 'field_display_on_floor_plans_pag';

  if (!shouldShowFavoriteResidences) {
    delete categoryData?.favorite_residences;
  }

  // Check if a level of care has any filterable categories (categories with >1 option)
  // Uses originalFilterData to ensure consistent behavior regardless of active filters
  const hasFilterableCategories = (locUuid) => {
    const locChildren = originalFilterData[locUuid]?.children;
    if (!locChildren) return false;

    // Get category keys, excluding ones we always hide
    const categoryKeys = Object.keys(locChildren).filter((key) => {
      if (key === 'level_of_care') return false;
      if (!platformShowOccupancy && key === 'residence_status') return false;
      if (!shouldShowFavoriteResidences && key === 'favorite_residences') return false;
      return true;
    });

    // Check if any remaining category has more than 1 option
    return categoryKeys.some((key) => {
      const childCount = locChildren[key]?.children
        ? Object.keys(locChildren[key].children).length
        : 0;
      return childCount > 1;
    });
  };

  /**
   * Sort function for filter items by weight, then alphabetically.
   */
  const sortByWeightThenLabel = ([, a], [, b]) => {
    // Sort by weight first
    if (a.weight !== b.weight) {
      return a.weight - b.weight;
    }
    // If weights are equal, sort alphabetically by label
    return (a.label).localeCompare(b.label);
  };

  /**
   * Sort function for level of care entries.
   * Puts the default level of care first, then sorts by weight, then alphabetically.
   */
  const sortLevelOfCare = ([keyA, a], [keyB, b]) => {
    // Default level of care always comes first
    if (keyA === defaultLevelOfCare) return -1;
    if (keyB === defaultLevelOfCare) return 1;
    // Otherwise, sort by weight then label
    if (a.weight !== b.weight) {
      return a.weight - b.weight;
    }
    return (a.label).localeCompare(b.label);
  };

  /**
   * Sort function for filter items alphabetically by label only.
   */
  const sortByLabelOnly = ([, a], [, b]) => (a.label).localeCompare(b.label);

  /**
   * Perform search against the Fuse index and returns formatted data.
   */
  const doMapFilterSearch = (params) => {
    if (!fuseIndex) {
      return;
    }

    const allDocs = fuseIndex.getIndex().docs;

    if (params) {
      // Format the filtered data sets.
      const {
        units,
        mergedLevelOfCare,
        resultAll,
      } = formatAndMergeFilteredData(
        allDocs,
        params,
        originalFilterData,
        activeCategoryFilter,
        residenceFavorites,
        showByFieldName,
        validFloorPlanUuids,
      );

      // If no results are returned, remove filter option that had no results.
      // This only happens when a unit's status is changed in YT and it was the only
      // one of it's status - leaving the filter results empty.
      // This will trigger a new search with no occupancy filter.
      if (resultAll.length === 0) {
        setActiveFilterSelections({});
      }

      // Update the UI with the merged filter data.
      setFormattedMapFilterData(mergedLevelOfCare);
      if (setMapActiveUnitData) {
        setMapActiveUnitData(units);
      }
      if (setResultFloorPlans) {
        const filteredFloorPlans = getFilterResultsByType('floor_plan', resultAll);
        setResultFloorPlans(filteredFloorPlans);
      }
    } else {
      // If no filters are active, revert to original data.
      setFormattedMapFilterData(originalFilterData);
      if (setMapActiveUnitData) {
        const { units } = FormattedResidenceFilterData(allDocs, showByFieldName, validFloorPlanUuids);
        setMapActiveUnitData(units);
      }
      if (setResultFloorPlans) {
        const filteredFloorPlans = getFilterResultsByType('floor_plan', allDocs);
        setResultFloorPlans(filteredFloorPlans);
      }
    }
  };

  const goToNextPanel = () => {
    setActiveSlidingPaneIndex(activeSlidingPaneIndex + 1);
  };

  const clearAllFilters = () => {
    setActiveFilterSelections({});
    setPendingSelections({});
  };

  const onCategoryBtnClick = (event, category) => {
    setActiveCategoryFilter(category);
    const clickTarget = event.target;

    // Check that the element clicked *isn't* the Remove button, then advance
    // to the next menu panel.
    if (!clickTarget.classList.contains('onImageDropdown__option__tag__remove')) {
      goToNextPanel();
    }
  };

  const isPending = (uuid) => {
    const pending = pendingSelections[activeCategoryFilter];
    const findIsPending = pending ? Object.prototype.hasOwnProperty.call(pending, uuid) : false;
    return isPending && findIsPending;
  };

  // When a selection is made on the detail panel, we will toggle that selection
  // in the pending selections.
  const onDetailBtnClick = (detailData) => {
    const updatedPendingSelections = updatePendingSelections(
      pendingSelections,
      activeCategoryFilter,
      detailData,
    );
    setPendingSelections(updatedPendingSelections);
  };

  // Use pre-formatted filter data from API if available (Floor Plans Page).
  // This skips the expensive client-side processing of map data.
  useEffect(() => {
    if (!preFormattedFilterData) {
      return;
    }

    const { levelOfCare, allLevelsOfCare, filterItems } = preFormattedFilterData;

    // Set critical data immediately for UI to render
    setFormattedMapFilterData(levelOfCare);
    setLevelOfCareNames(allLevelsOfCare);
    setOriginalFilterData(levelOfCare);

    // Create Fuse.js index from pre-formatted filter items
    const fuseOptions = {
      includeScore: true,
      threshold: 0.0,
      keys: ['bedroom_data', 'floor_plan', 'level_of_care', 'map_building_grouping', 'residence_status', 'floor_plan_category'],
    };

    const createFuseIndex = () => {
      const fuse = new Fuse(filterItems, fuseOptions);
      setFuseIndex(fuse);
    };

    // Use requestIdleCallback if available, otherwise defer with setTimeout
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(createFuseIndex, { timeout: 2000 });
    } else {
      setTimeout(createFuseIndex, 0);
    }
  }, [preFormattedFilterData]);

  // Fetch Map Filter Data from Drupal and create Fusejs search index.
  // Performance: Defer Fuse.js indexing to idle time to speed up initial render
  // Only used when preFormattedFilterData is not available (e.g., Interactive Map).
  useEffect(() => {
    // Skip if we have pre-formatted data from API
    if (preFormattedFilterData) {
      return;
    }

    // Guard: Only process if mapFilterData is available
    if (!mapFilterData || mapFilterData.length === 0) {
      return;
    }

    const {
      levelOfCare,
      units,
      allLevelsOfCare,
    } = FormattedResidenceFilterData(mapFilterData, showByFieldName, validFloorPlanUuids);

    // Set critical data immediately for UI to render
    setFormattedMapFilterData(levelOfCare);
    setLevelOfCareNames(allLevelsOfCare);
    setOriginalFilterData(levelOfCare);

    // Save indexed data for determining active units for Interactive Map only.
    if (setMapActiveUnitData) {
      setMapActiveUnitData(units);
    }

    // Defer expensive Fuse.js indexing to idle time or next tick
    // This prevents blocking the main thread during initial page load
    const fuseOptions = {
      includeScore: true,
      threshold: 0.0,
      keys: ['bedroom_data', 'floor_plan', 'level_of_care', 'map_building_grouping', 'residence_status', 'floor_plan_category'],
    };

    const createFuseIndex = () => {
      const fuse = new Fuse(mapFilterData, fuseOptions);
      setFuseIndex(fuse);
    };

    // Use requestIdleCallback if available, otherwise defer with setTimeout
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(createFuseIndex, { timeout: 2000 });
    } else {
      setTimeout(createFuseIndex, 0);
    }
  }, [mapFilterData, showByFieldName, setMapActiveUnitData, validFloorPlanUuids, preFormattedFilterData]);

  // Update filter data when a residence status is changed on the YT side.
  useEffect(() => {
    if (!isMountedRef.current) return;

    if (changedUnits?.length > 0) {
      const fuseDocs = fuseIndex?.getIndex()?.docs;

      // Guard against fuseDocs being undefined (can happen when returning to map)
      if (!fuseDocs) return;

      // Track if we need to update originalFilterData with new status options
      let needsOriginalDataUpdate = false;
      const originalFilterDataCopy = structuredClone(originalFilterData);

      // Track old statuses that need to be checked for removal
      const oldStatusesToCheck = [];

      changedUnits?.forEach((changedUnit) => {
        const changedUnitSvgId = changedUnit?.data?.attributes?.field_svg_id;
        const changedUnitRelationships = changedUnit?.data?.relationships;

        // The Fuse docs use 'svg_id' (from Drupal filterData), not 'field_svg_id'
        const fuseDoc = fuseDocs.find(
          (doc) => doc.svg_id === changedUnitSvgId || doc.field_svg_id === changedUnitSvgId,
        );
        const fuseDocCopy = structuredClone(fuseDoc);

        // Track the old status before we update it (for potential removal)
        if (fuseDoc?.residence_status?.uuid && fuseDoc?.level_of_care?.uuid) {
          oldStatusesToCheck.push({
            statusUuid: fuseDoc.residence_status.uuid,
            locUuid: fuseDoc.level_of_care.uuid,
          });
        }

        // Remove stale unit from the index.
        fuseIndex.remove(
          (doc) => doc.svg_id === changedUnitSvgId || doc.field_svg_id === changedUnitSvgId,
        );

        // Add refreshed unit back to index.
        if (fuseDocCopy) {
          const changedUnitStatusUuid = changedUnitRelationships?.field_status?.data?.id;
          const changedUnitStatusPhrase = residenceStatusOptions.find(
            (status) => status.id === changedUnitStatusUuid,
          );
          // Format: Uuid - Label
          const changedUnitStatus = `${changedUnitStatusUuid} - ${changedUnitStatusPhrase?.name}`;
          fuseDocCopy.occupancy = changedUnitStatus;

          // Also update the residence_status object for filter processing
          fuseDocCopy.residence_status = {
            uuid: changedUnitStatusUuid,
            name: changedUnitStatusPhrase?.name,
            weight: changedUnitStatusPhrase?.weight || 0,
          };

          fuseIndex.add(fuseDocCopy);

          // Check if this status exists in originalFilterData for this unit's level of care.
          // If not, add it so the filter options include the new status.
          const unitLocUuid = fuseDocCopy?.level_of_care?.uuid;
          if (unitLocUuid && changedUnitStatusUuid && changedUnitStatusPhrase) {
            const locData = originalFilterDataCopy[unitLocUuid];
            const existingStatus = locData?.children?.residence_status?.children?.[changedUnitStatusUuid];

            if (!existingStatus && locData?.children?.residence_status?.children) {
              // Add the new status to originalFilterData
              const statusWeight = parseInt(changedUnitStatusPhrase.weight, 10);
              originalFilterDataCopy[unitLocUuid].children.residence_status.children[changedUnitStatusUuid] = {
                label: decodeHtml(changedUnitStatusPhrase.name || ''),
                uuid: changedUnitStatusUuid,
                weight: Number.isNaN(statusWeight) ? 100 : statusWeight,
                meta: fuseDocCopy,
              };
              needsOriginalDataUpdate = true;
            }
          }
        }
      });

      // Check if any old statuses should be removed (no units left with that status)
      const updatedFuseDocs = fuseIndex.getIndex().docs;
      oldStatusesToCheck.forEach(({ statusUuid, locUuid }) => {
        // Check if any units still have this status in this level of care
        const unitsWithStatus = updatedFuseDocs.filter(
          (doc) => doc.residence_status?.uuid === statusUuid
            && doc.level_of_care?.uuid === locUuid,
        );

        // If no units have this status anymore, remove it from originalFilterData
        if (unitsWithStatus.length === 0) {
          const locData = originalFilterDataCopy[locUuid];
          if (locData?.children?.residence_status?.children?.[statusUuid]) {
            delete originalFilterDataCopy[locUuid].children.residence_status.children[statusUuid];
            needsOriginalDataUpdate = true;
          }
        }
      });

      // Update originalFilterData if status options were added or removed
      if (needsOriginalDataUpdate) {
        setOriginalFilterData(originalFilterDataCopy);
      }

      // Update active units for Interactive Map only.
      if (isMountedRef.current) {
        if (Object.keys(activeFilterSelections).length) {
          doMapFilterSearch(activeFilterSelections);
        } else {
          doMapFilterSearch();
        }
      }
    }
  }, [changedUnits]);

  // Auto-select single level of care and skip to categories pane
  useEffect(() => {
    if (!isMountedRef.current) return;

    if (hasSingleLevelOfCare && singleLevelOfCareUuid) {
      setActiveLevelOfCareUuid(singleLevelOfCareUuid);
      setActiveSlidingPaneIndex(1);
      // Set paneWidths[0] to 0 so slider positioning works correctly
      // (since the first pane isn't rendered)
      setPaneWidths((prev) => ({ ...prev, 0: 0 }));
    }
  }, [hasSingleLevelOfCare, singleLevelOfCareUuid, setActiveLevelOfCareUuid]);

  // When the user returns to the LOC panel, clear active filters.
  // Skip this logic if there's only one level of care (user can't navigate back)
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (hasSingleLevelOfCare) return;

    if (activeSlidingPaneIndex === 0) {
      // Set active selections to an empty object.
      if (isMountedRef.current) {
        clearAllFilters();
      }

      // Set map back to default Level of Care
      if (isMountedRef.current) {
        setActiveLevelOfCareUuid(defaultLevelOfCare);
      }
    }
  }, [activeSlidingPaneIndex, defaultLevelOfCare, hasSingleLevelOfCare]);

  // When the active selections are made, filter the remaining data that should
  // be visible to the user. If there are no active filters, reset back
  // to original dataset.
  useEffect(() => {
    if (!isMountedRef.current) return;

    if (Object.keys(activeFilterSelections).length) {
      doMapFilterSearch(activeFilterSelections);
    } else {
      doMapFilterSearch();
    }

    if (isMountedRef.current) {
      setHasActiveFilters(Object.keys(activeFilterSelections).length > 0);
    }
  }, [
    activeFilterSelections,
    fuseIndex,
    originalFilterData,
    activeCategoryFilter,
    residenceFavorites,
    setMapActiveUnitData,
    setResultFloorPlans,
  ]);

  // Auto-apply filters whenever selections change.
  useEffect(() => {
    if (!isMountedRef.current) return;

    const newFilterSelections = { ...activeFilterSelections };
    if (newFilterSelections[activeCategoryFilter]) {
      delete newFilterSelections[activeCategoryFilter];
    }
    if (pendingSelections[activeCategoryFilter]) {
      newFilterSelections[activeCategoryFilter] = pendingSelections[activeCategoryFilter];
    }

    if (isMountedRef.current) {
      setActiveFilterSelections(newFilterSelections);
    }
  }, [pendingSelections, activeCategoryFilter]);

  // Guard: Show loading state while filter data is loading
  // This check must come AFTER all hooks to comply with Rules of Hooks
  // Use preFormattedFilterData if available, otherwise fall back to mapFilterData
  const hasFilterData = preFormattedFilterData || (mapFilterData && mapFilterData.length > 0);
  if (!hasFilterData) {
    return (
      <SlidingPanesWidget
        activeSlidingPaneIndex={0}
        paneWidths={{ 0: 100 }}
      >
        <SlidingPane
          key="loading"
          index={0}
          activeSlidingPaneIndex={0}
          setActiveSlidingPaneIndex={() => { }}
          paneWidths={{ 0: 100 }}
          setPaneWidths={() => { }}
        >
          <div className="onImageDropdown__option residenceFilter__loading">
            Loading filters
            <span className="residenceFilter__loadingDot">.</span>
            <span className="residenceFilter__loadingDot residenceFilter__loadingDot--second">.</span>
            <span className="residenceFilter__loadingDot residenceFilter__loadingDot--third">.</span>
          </div>
        </SlidingPane>
      </SlidingPanesWidget>
    );
  }

  return (
    <SlidingPanesWidget
      activeSlidingPaneIndex={activeSlidingPaneIndex}
      paneWidths={paneWidths}
    >
      {/* Levels of Care Panel - hidden when only one level of care */}
      {!hasSingleLevelOfCare && (
        <SlidingPane
          key="levelsOfCare"
          index={0}
          activeSlidingPaneIndex={activeSlidingPaneIndex}
          setActiveSlidingPaneIndex={setActiveSlidingPaneIndex}
          setPaneWidths={setPaneWidths}
        >
          {levelOfCareEntries
            .sort(sortLevelOfCare)
            .map(([locKey, locData]) => {
              const locHasFilterableCategories = hasFilterableCategories(locKey);
              return (
                <button
                  key={locKey}
                  type="button"
                  className={classNames(
                    'onImageDropdown__option',
                    { 'onImageDropdown__option--more': locHasFilterableCategories },
                  )}
                  onClick={() => {
                    setActiveLevelOfCareUuid(locKey);
                    if (locHasFilterableCategories) {
                      goToNextPanel();
                    }
                  }}
                >
                  {locData.label}
                </button>
              );
            })}
        </SlidingPane>
      )}

      {/* Categories Panel */}
      <SlidingPane
        key="categories"
        index={1}
        activeSlidingPaneIndex={activeSlidingPaneIndex}
        backBtnText={hasSingleLevelOfCare ? null : (windowWidth < 768 ? 'Back' : 'Back to Levels of Care')}
        setActiveSlidingPaneIndex={setActiveSlidingPaneIndex}
        setPaneWidths={setPaneWidths}
      >
        {categoryData && Object.values(categoryData)
          .filter((category) => {
            // Always show categories with active selections
            if (activeFilterSelections[category.id]) {
              return true;
            }
            // Use original (unfiltered) data to check child count
            const originalChildren = originalFilterData[activeLevelOfCareUuid]?.children?.[category.id]?.children;
            const childCount = originalChildren ? Object.keys(originalChildren).length : 0;
            return childCount > 1;
          })
          .map((category) => (
            <button
              key={category.id}
              type="button"
              className="onImageDropdown__option onImageDropdown__option--more onImageDropdown__option--tags"
              onClick={(event) => onCategoryBtnClick(event, category.id)}
            >
              <div className="onImageDropdown__optionContent">

                <div className="onImageDropdown__optionName">
                  {category.label}
                </div>

                {activeFilterSelections[category.id] && (
                  <ResidenceFilterOptionTags
                    activeFilterSelections={activeFilterSelections}
                    setActiveFilterSelections={setActiveFilterSelections}
                    categoryId={category.id}
                  />
                )}

              </div>
            </button>
          ))}

        {hasActiveFilters && windowWidth >= 768 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="residenceFilter__clearFiltersBtn"
          >
            Clear all filters
          </button>
        )}

      </SlidingPane>

      {/* Details Panel */}
      <SlidingPane
        key="details"
        index={2}
        activeSlidingPaneIndex={activeSlidingPaneIndex}
        backBtnClassName="onImageDropdown__option"
        backBtnText={windowWidth < 768 ? 'Back' : 'Back to Categories'}
        // clearAllFilters={hasActiveFilters ? clearAllFilters : null}
        setActiveSlidingPaneIndex={setActiveSlidingPaneIndex}
        setPaneWidths={setPaneWidths}
      >
        {(detailsData && Object.keys(detailsData).length > 0)
          ? Object.entries(detailsData)
            .sort(activeCategoryFilter === 'floor_plan' || platform === 'page' || platform === 'siteSee' ? sortByLabelOnly : sortByWeightThenLabel)
            .map(([uuid, detailData]) => (
              <button
                key={uuid}
                className={
                  classNames(
                    'onImageDropdown__option onImageDropdown__option--checkbox',
                    { 'onImageDropdown__option--selected': isPending(uuid) },
                  )
                }
                onClick={() => onDetailBtnClick(detailData)}
                data-id={uuid}
                type="button"
              >
                <span
                  className={`onImageDropdown__optionCheckbox onImageDropdown__optionCheckbox--${isPending(uuid) ? 'active' : 'disabled'}`}
                  style={createDetailBtnStyle(detailData, activeCategoryFilter)}
                >
                  {isPending(uuid) && (
                    <svg width="21" height="18" viewBox="0 0 21 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1.5 8.5L7.75251 15.5L19.0144 2.18506" stroke={getContrastTextColorValue(detailData?.keyColor) || 'white'} strokeWidth="2" strokeLinecap="square" />
                    </svg>
                  )}
                </span>

                {detailData?.label}
              </button>
            )) : (
            <div className="residenceFilter__noResults onImageDropdown__option">
              There are no options available for this category.
            </div>
          )}

        {/* { hasActiveFilters && windowWidth >= 768 && (
          <button
            type="button"
            className="onImageDropdown__option residenceFilter__clearFiltersBtn"
            onClick={clearAllFilters}
          >
            Clear all filters
          </button>
        )} */}

      </SlidingPane>

    </SlidingPanesWidget>
  );
};

ResidenceFilterContent.propTypes = {
  showByFieldName: PropTypes.string,
};

ResidenceFilterContent.defaultProps = {
  showByFieldName: null,
};

export default ResidenceFilterContent;
